import {
    ref,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
    off,
    update,
    get,
    serverTimestamp,
    onValue
} from 'firebase/database';
import { func as functions, rtdb } from './config';
import type { Participant, Position, TenantApp } from '@inloco/shared';
import type { RoomSessionWithoutId } from '../types/room-session';
import { httpsCallable } from 'firebase/functions';

const prefixRoomFunc = 'roomFunctions';
const prefixBleFunc = 'bleFunctions';

// Function to create a new room
export const createRoom = async (app: TenantApp, roomSession: RoomSessionWithoutId, position: Position) => {
    const createRoomCallable = httpsCallable(functions, `${prefixRoomFunc}-createRoom`);
    const result = await createRoomCallable({
        segmentId: app.segmentId,
        roomSession,
        position
    });
    return result.data as { roomId: string; createdAt: number; expiresAt: number };
};

// Function to join an existing room
export const joinRoom = async (app: TenantApp, roomId: string, username: string, position: Position) => {
    const joinRoomCallable = httpsCallable(functions, `${prefixRoomFunc}-joinRoom`);
    const result = await joinRoomCallable({ segmentId: app.segmentId, roomId, username, position });
    return result.data as { createdAt: number; expiresAt: number; serverTime: number };
};

// Function to update user position
export const updatePosition = async (
    app: TenantApp,
    roomId: string,
    userId: string,
    position: Position
): Promise<void> => {
    const { segmentId } = app;

    const positionRef = ref(rtdb, `${segmentId}/loc/${roomId}/${userId}`);

    // Check if participant already exists
    const snapshot = await get(positionRef);
    const existingData = snapshot.val();

    // If participant doesn't exist yet, do nothing
    if (!existingData) {
        console.warn(`Attempt to update position for user ${userId} who has not yet joined`);
        return;
    }

    // Round coordinates to 6 decimal places
    const roundedPosition = {
        lt: Math.round(position.lt * 1000000) / 1000000,
        lg: Math.round(position.lg * 1000000) / 1000000,
        ac: Math.round((position as any).ac || 0)
    };

    await update(positionRef, {
        ...roundedPosition,
        lu: serverTimestamp()
    });
};

// Function to remove a user from the room
export const removeUserFromRoom = async (app: TenantApp, roomId: string) => {
    const removeUserCallable = httpsCallable(functions, `${prefixRoomFunc}-removeUserFromRoom`);
    await removeUserCallable({ segmentId: app.segmentId, roomId });
};

// Function to save BLE device in a room
export const addBleDeviceToRoom = async (app: TenantApp, roomId: string, macAddress: string, name?: string) => {
    const addBleDeviceCallable = httpsCallable(functions, `${prefixBleFunc}-addBleDeviceToRoom`);
    const result = await addBleDeviceCallable({
        segmentId: app.segmentId,
        roomId,
        macAddress,
        name
    });
    return result.data as { createdAt: number; expiresAt: number; serverTime: number };
}

// Function to subscribe to room updates
// Function to subscribe to room updates
export const subscribeToRoom = (
    app: TenantApp,
    userId: string,
    roomId: string,
    callback: (participants: Participant[]) => void
): (() => void) => {
    const { segmentId } = app;
    const locRef = ref(rtdb, `${segmentId}/loc/${roomId}`);

    const participantsMap = new Map<string, any>(); // Anagrafe (n, t)
    const bleStatus: Record<string, any> = {};      // Telemetria (gt, lu, s)

    // Mappa per tenere traccia delle funzioni di unsubscribe dei singoli BLE
    const bleUnsubscribeMap = new Map<string, () => void>();

    const notifyChange = () => {
        const finalParticipants: Participant[] = [];

        participantsMap.forEach((data, id) => {
            if (data.t === 'd') {
                // Smartphone
                if (data.lt && data.lg) {
                    finalParticipants.push({
                        id,
                        ac: data.ac || 0,
                        name: data.n,
                        type: 'device',
                        position: { lt: data.lt, lg: data.lg },
                        lastUpdate: data.lu
                    });
                }
            } else if (data.t === 'b') {
                // BLE
                const status = bleStatus[id];
                if (status) {
                    const hash = id.split('').reduce((acc, char) => ((acc << 5) - acc) + char.charCodeAt(0), 0);
                    const angle = (Math.abs(hash) % 360) * (Math.PI / 180);
                    const radiusDeg = 8 / 111000;
                    const offsetLt = radiusDeg * Math.cos(angle);
                    const offsetLg = radiusDeg * Math.sin(angle);

                    finalParticipants.push({
                        id,
                        name: data.n,
                        ac: status.d || 0,
                        type: 'beacon',
                        position: {
                            lt: status.lt + offsetLt,
                            lg: status.lg + offsetLg
                        },
                        lastUpdate: status.lu,
                        signal: status.s,
                        poiId: status.sk
                    });
                }
            }
        });

        callback(finalParticipants);
    };

    // Listen for who enters/exits from room (LOC Node)
    const handleChildAdded = (snapshot: any) => {
        const id = snapshot.key;
        const data = snapshot.val();

        if (id !== userId) {
            participantsMap.set(id, data);

            // If the new participant is a BLE device, attach a specific listener for it
            if (data.t === 'b') {
                const specificBleRef = ref(rtdb, `${segmentId}/ble/${id}`);
                const unsubscribeBle = onValue(specificBleRef, (bleSnapshot) => {
                    if (bleSnapshot.exists()) {
                        bleStatus[id] = bleSnapshot.val();
                    } else {
                        delete bleStatus[id];
                    }
                    notifyChange();
                });

                // Save the function to be able to turn it off later
                bleUnsubscribeMap.set(id, unsubscribeBle);
            } else {
                notifyChange();
            }
        }
    };

    const handleChildChanged = (snapshot: any) => {
        if (snapshot.key !== userId) {
            participantsMap.set(snapshot.key, snapshot.val());
            notifyChange();
        }
    };

    const handleChildRemoved = (snapshot: any) => {
        const id = snapshot.key;
        participantsMap.delete(id);

        // If it was a BLE device, detach the listener for its telemetry
        if (bleUnsubscribeMap.has(id)) {
            const unsubscribeBle = bleUnsubscribeMap.get(id);
            if (unsubscribeBle) unsubscribeBle(); // Detach the connection

            bleUnsubscribeMap.delete(id);
            delete bleStatus[id]; // Clean the telemetry cache
        }

        notifyChange();
    };

    onChildAdded(locRef, handleChildAdded);
    onChildChanged(locRef, handleChildChanged);
    onChildRemoved(locRef, handleChildRemoved);

    // General cleanup when leaving the room / unmounting the component
    return () => {
        // Turn off all individual BLE listeners
        bleUnsubscribeMap.forEach((unsubscribe) => unsubscribe());
        bleUnsubscribeMap.clear();

        // Turn off room listeners
        off(locRef, 'child_added', handleChildAdded);
        off(locRef, 'child_changed', handleChildChanged);
        off(locRef, 'child_removed', handleChildRemoved);
    };
};